import { useEffect, useState } from 'react';
import _ from 'lodash';

import {useConfigurationQuery} from "../../../../../../../theSource/queries";


interface GoogleMapProps {
  id: string;
  style: object;
  maxInitialZoom: number;
  markers: object[];
}

export const GoogleMap = ({style, maxInitialZoom, id, markers}: GoogleMapProps) => {
  const [currentInfoWindow, setCurrentInfoWindow] = useState<any>(null);
  const [map, setMap] = useState<any | null>(null);
  const [oms, setOms] = useState<any | null>(null);
  const [existingMarkers, setExistingMarkers] = useState<any[]>([]);

  const {configuration} = useConfigurationQuery();

  const onScriptLoad = () => {
    if (window.google?.maps) {
      const mapData = new window.google.maps.Map(document.getElementById(id), {
        zoom: 12,
        streetViewControl: false,
        zoomControl: false,
        mapTypeControl: false,
        rotateControl: false,
        fullscreenControl: false,
        keyboardShortcuts: false,
        scaleControl: false,
        clickableIcons: false
      });
      if (mapData && OverlappingMarkerSpiderfier) {
        const omsObj = new OverlappingMarkerSpiderfier(mapData,
          {
            markersWontMove: true,
            markersWontHide: true,
            keepSpiderfied: true,
            circleSpiralSwitchover: Infinity,
            nearbyDistance: 1
          });
        omsObj.addListener('format', (marker: any, status: any) => {
          switch (status) {
            case 'UNSPIDERFIABLE':
              marker.setLabel(null);
              break;
            case 'SPIDERFIED':
              marker.setLabel(null);
              break;
            case 'SPIDERFIABLE':
              marker.setLabel({text: '+', color: 'white'});
              break;
          }
        });
        omsObj.addListener('unspiderfy', () => {
          if (currentInfoWindow) {
            currentInfoWindow.close();
          }
        });

        const listener = window.google.maps.event.addListener(mapData, 'idle', () => {
          const currentInfoWindow = null;
          setMap(mapData);
          setOms(omsObj);
          setCurrentInfoWindow(currentInfoWindow);
          window.google.maps.event.removeListener(listener);
        });
      }
    }
  };

  const needToUpdateMarkers = () => {
    return map &&
      markers &&
      !_.isEqual(existingMarkers, markers);
  };

  const buildMapMarker = (marker, newMap, googleMaps) => {
    const mapMarker = new googleMaps.Marker({
      map: newMap,
      position: marker.position,
    });
    const mapInfoWindow = new googleMaps.InfoWindow({content: marker.infoPopup});
    googleMaps.event.addListener(mapMarker, 'spider_click', () => {
      if (currentInfoWindow) {
        currentInfoWindow.close();
      }
      mapInfoWindow.open(newMap, mapMarker);
      setCurrentInfoWindow(mapInfoWindow);
    });

    return mapMarker;
  };

  const clearMapMarker = (marker, googleMaps) => {
    if (currentInfoWindow) {
      currentInfoWindow.close();
    }
    oms.removeAllMarkers();
    googleMaps.event.clearInstanceListeners(marker);
  };

  const updateMarkers = () => {
    if (needToUpdateMarkers()) {
      const newMap = map;
      const newMarkers = markers;
      const googleMaps = window.google.maps;

      if (newMap.markers) {
        newMap.markers.forEach(marker => clearMapMarker(marker, googleMaps));
      }
      newMap.markers = newMarkers.map(marker => buildMapMarker(marker, newMap, googleMaps));

      if (newMap.markers.length > 0) {
        const bounds = new googleMaps.LatLngBounds();
        newMap.markers.forEach(marker => {
          oms.trackMarker(marker);
        });
        newMap.markers.forEach(marker => {
          bounds.extend(marker.position);
        });

        if (maxInitialZoom) {
          newMap.initialZoom = true;
          googleMaps.event.addListener(newMap, 'zoom_changed', () => {
            googleMaps.event.addListenerOnce(newMap, 'bounds_changed', () => {
              if (newMap.getZoom() > maxInitialZoom && newMap.initialZoom === true) {
                newMap.setZoom(maxInitialZoom);
                newMap.initialZoom = false;
              }
            });
          });
        }
        newMap.fitBounds(bounds);
      }

      setExistingMarkers(newMarkers);
    }
  };

  useEffect(() => {
    const loadScripts = async (urls: string[]): Promise<void[]> => {
      const promises = urls.map(url => new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${url}"]`)) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = (error) => reject(error);
        document.body.appendChild(script);
      }));

      return Promise.all(promises);
    };

    const initMap = async () => {
      if (!window.google) {
        const GOOGLE_MAP_API_KEY = configuration?.googleMapsApiKey;
        const scriptsToLoad = [
          `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}`,
          'https://cdnjs.cloudflare.com/ajax/libs/OverlappingMarkerSpiderfier/1.0.3/oms.min.js'
        ];

        await loadScripts(scriptsToLoad);
        onScriptLoad();
      } else {
        onScriptLoad();
      }
    };
    initMap();
  // eslint-disable-next-line
  }, []);

  return (
    <>
      {updateMarkers()}
      <div style={style} id={id}/>
    </>

  );
};
